import React, { useRef } from "react";
import { useSubmit } from "react-router";
import { Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogle, faFacebook } from "@fortawesome/free-brands-svg-icons";
import { GenericOAuth2 } from "@capacitor-community/generic-oauth2";
import { renderLoadingProgressToast } from "components/shared/Toasts";
import axios from "axios";

export async function action({ request }) {
    const { provider, oauth2Options, returnUrl } = await request.json();
    const result = await renderLoadingProgressToast(async () => {
        const response = await GenericOAuth2.authenticate(oauth2Options);
        let payload = null;
        if (provider === "google") {
            payload = response["access_token_response"];
        } else if (provider === "facebook") {
            payload = {
                token_type: "Bearer",
                ...response,
            };
        }
        await axios.post(`account/${provider}/login`, payload);
    })
        .then(() => ({ success: true }))
        .catch((error) => {
            if (error.message === "USER_CANCELLED") {
                return Promise.resolve({
                    success: false,
                    code: error.message,
                    reason: "Cancelled login by user",
                });
            }
            return Promise.reject(error);
        });

    // Redirect to return URL if provided, otherwise go to home page

    if (result.success) {
        window.location.replace(returnUrl || "/");
        return null;
    }

    return result;
}

const OAuthSignIn = ({ returnUrl }) => {
    const isSubmittingRef = useRef(false);
    const submit = useSubmit();
    const getOAuthOptionsByProvider = async (provider) => {
        // prelogin request, send original refererer, return url to backend and store in session
        // get back state hash string
        const resp = await axios.get(`account/${provider}/login`, {
            params: returnUrl ? { next: returnUrl } : {},
        });
        if (resp.status !== 200) {
            console.error("OAuth rejected", resp.statusText);
            return;
        }
        const state = await resp.data;

        if (provider === "google") {
            return {
                authorizationBaseUrl:
                    "https://accounts.google.com/o/oauth2/auth",
                accessTokenEndpoint:
                    "https://www.googleapis.com/oauth2/v4/token",
                scope: "email profile",
                resourceUrl: "https://www.googleapis.com/userinfo/v2/me",
                web: {
                    appId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                    responseType: "code",
                    accessTokenEndpoint: "",
                    redirectUrl: process.env.REACT_APP_OAUTH_REDIRECT_URL,
                    windowTarget: "_top",
                    state,
                },
                android: {
                    appId: process.env.REACT_APP_GOOGLE_ANDROID_CLIENT_ID,
                    responseType: "code",
                    redirectUrl:
                        process.env.REACT_APP_GOOGLE_ANDROID_CUSTOM_URL_SCHEME +
                        ":/",
                },
                ios: {
                    appId: process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID,
                    responseType: "code",
                    redirectUrl:
                        process.env.REACT_APP_GOOGLE_IOS_CUSTOM_URL_SCHEME +
                        ":/",
                },
            };
        } else if (provider === "facebook") {
            return {
                appId: process.env.REACT_APP_FACEBOOK_CLIENT_ID,
                authorizationBaseUrl: "https://www.facebook.com/dialog/oauth",
                resourceUrl: "https://graph.facebook.com/me",
                scope: "public_profile,email",
                web: {
                    responseType: "code",
                    accessTokenEndpoint: "",
                    redirectUrl: process.env.REACT_APP_OAUTH_REDIRECT_URL,
                    windowTarget: "_top",
                    state,
                },
                android: {
                    customHandlerClass:
                        "com.wishliz.AndroidFacebookOAuth2Handler",
                },
                ios: {
                    customHandlerClass: "App.IOsFacebookOAuth2Handler",
                },
            };
        }
        console.error("unknown provider:", provider);
        return;
    };

    const login = async (provider) => {
        isSubmittingRef.current = true;
        const oauth2Options = await getOAuthOptionsByProvider(provider);
        if (!oauth2Options) {
            console.error("Unable to get oauth option for provider", provider);
            return;
        }

        try {
            submit(
                {
                    provider,
                    oauth2Options,
                    returnUrl,
                },
                {
                    action: "/account/signin",
                    method: "post",
                    encType: "application/json",
                }
            );
        } catch (ex) {
            console.error("OAuth rejected", ex);
        } finally {
            isSubmittingRef.current = false;
        }
    };

    return (
        <>
            <Card
                className="mt-3 mx-auto"
                style={{
                    width: "100%",
                    maxWidth: "300px",
                    minWidth: "0",
                    boxSizing: "border-box",
                }}
            >
                <Card.Header>Sign in with:</Card.Header>
                <Card.Body>
                    <div className="d-grid gap-2">
                        <Button
                            variant="primary"
                            disabled={isSubmittingRef.current}
                            onClick={() => login("google")}
                        >
                            <FontAwesomeIcon icon={faGoogle} /> Google
                        </Button>
                        <Button
                            variant="primary"
                            disabled={isSubmittingRef.current}
                            onClick={() => login("facebook")}
                        >
                            <FontAwesomeIcon icon={faFacebook} /> Facebook
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

export default OAuthSignIn;
